<template>
  <div v-if="canLoad" :style="{ width: '100%' }">
    <template v-if="editors[sectionKey] && editors[sectionKey].hasOwnProperty(id)">
      <div :id="`custom-${sectionKey}-${id}`" :class="getClassName" v-html="getEditorContent" />
      <slot v-if="canShowConditional" name="conditional-content" />
    </template>
    <template v-else-if="!isEmptyContent">
      <template v-if="textEditors && textEditors[sectionKey] && textEditors[sectionKey][id] && textEditors[sectionKey][id].html">
        <div :id="`custom-${sectionKey}-${id}`" :class="getClassName" v-html="textEditors[sectionKey][id].html" />
        <slot v-if="canShowConditional" name="conditional-content" />
      </template>
      <template v-else-if="textEditors && textEditors[sectionKey] && textEditors[sectionKey][id] && textEditors[sectionKey][id].hasOwnProperty('html') && textEditors[sectionKey][id].html !== null">
        <div :id="`custom-${sectionKey}-${id}`" :class="getClassName" v-html="!live && !preview && !renderOnly ? froalaDefaultContent : ''" />
        <slot v-if="canShowConditional" name="conditional-content" />
      </template>
      <template v-else>
        <div :id="`custom-${sectionKey}-${id}`" :class="getClassName">
          <slot name="default-content" />
        </div>
        <slot name="conditional-content" />
      </template>
    </template>
    <template v-else>
      <div :id="`custom-${sectionKey}-${id}`" :class="getClassName" v-html="!live && !preview && !renderOnly ? froalaDefaultContent : ''" />
      <slot v-if="canShowConditional" name="conditional-content" />
    </template>
  </div>
</template>

<script>
import FroalaEditor from 'froala-editor'
import { FROALA_CONFIG, FROALA_DEFAULT_CONTENT } from '@src/constants'
import { getParentDOM } from '@src/helper'
export default {
  props: {
    id: { required: true, type: String, default: null },
    values: { required: true, type: Array, default: null },
    sectionKey: { required: true, type: String, default: null },
    live: { required: true, type: Boolean, default: false },
    preview: { required: true, type: Boolean, default: false },
    renderOnly: { type: Boolean, default: false },
    editors: { type: Object, default: null },
    textEditors: { type: Object, default: null },
    enableImage: { type: Boolean, default: false }
  },
  data() {
    return {
      textEditorId: null,
      textEditorObject: null,
      froalaDefaultContent: FROALA_DEFAULT_CONTENT,
      showEditor: false,
      wrapElement: null
    }
  },
  computed: {
    canLoad() {
      return this.id && this.sectionKey && this.values && this.values.length && this.editors
    },
    checkTextEditor() {
      return !!(this.textEditors && this.textEditors[this.sectionKey])
    },
    isEmptyContent() {
      let isEmpty = true
      for (let i = 0; i < this.values.length; i++) {
        if (this.values[i]) {
          isEmpty = false
          break
        }
      }
      return isEmpty
    },
    getClassName() {
      let className = ['fr-view']
      if (!this.live && !this.preview && !this.renderOnly) {
        className.push('custom-textEditors')
      } else if (
        this.textEditors &&
        this.textEditors[this.sectionKey] &&
        this.textEditors[this.sectionKey][this.id] &&
        this.textEditors[this.sectionKey][this.id].hasOwnProperty('html') &&
        !this.textEditors[this.sectionKey][this.id].html
      ) {
        className.push('empty-content')
      } else if (
        this.editors[this.sectionKey] &&
        this.editors[this.sectionKey].hasOwnProperty(this.id) &&
        !this.editors[this.sectionKey][this.id]
      ) {
        className.push('empty-content')
      } else if ((!this.editors[this.sectionKey] || !this.editors[this.sectionKey][this.id]) && this.isEmptyContent) {
        className.push('empty-content')
      }
      return className.join(' ')
    },
    canShowConditional() {
      if (
        this.textEditors &&
        this.textEditors[this.sectionKey] &&
        this.textEditors[this.sectionKey][this.id] &&
        this.textEditors[this.sectionKey][this.id].html
      ) {
        return true
      }
      if (this.editors[this.sectionKey] && this.editors[this.sectionKey][this.id]) {
        return true
      }
      if (!this.live && !this.preview && !this.renderOnly) {
        return true
      }
      return false
    },
    getEditorContent() {
      if (
        this.textEditors &&
        this.textEditors[this.sectionKey] &&
        this.textEditors[this.sectionKey][this.id] &&
        this.textEditors[this.sectionKey][this.id].html
      ) {
        return this.textEditors[this.sectionKey][this.id].html
      }
      if (this.editors[this.sectionKey] && this.editors[this.sectionKey][this.id]) {
        return this.editors[this.sectionKey][this.id]
      }
      if (!this.live && !this.preview && !this.renderOnly) {
        return this.froalaDefaultContent
      }
      return ''
    }
  },
  watch: {
    checkTextEditor: {
      handler(val) {
        if (val && !this.live && !this.preview && !this.renderOnly && !this.textEditorObject) {
          this.$nextTick(() => {
            this.initTextEditors()
          })
        }
      }
    }
  },
  mounted() {
    if (!this.live && !this.preview && !this.renderOnly) {
      this.wrapElement = document.getElementById('classic-editor-toolbar')
      this.$nextTick(() => {
        this.initTextEditors()
        document.body.addEventListener('mousedown', this.manageTextEditors)  
      })
    }
  },
  beforeDestroy() {
    if (!this.live && !this.preview && !this.renderOnly) {
      if (this.wrapElement) {
        this.wrapElement.classList.remove('active')
      }
      if (this.textEditorObject) {
        this.textEditorObject.removeEventListener('click', this.initTextEditor)
      }
      document.body.removeEventListener('mousedown', this.manageTextEditors)
      if (
        this.textEditors &&
        this.textEditors[this.sectionKey] &&
        this.textEditors[this.sectionKey][this.id] &&
        this.textEditors[this.sectionKey][this.id].controller
      ) {
        const html = this.textEditors[this.sectionKey][this.id].controller.html.get(true)
        this.textEditors[this.sectionKey][this.id].controller.destroy()
        this.textEditors[this.sectionKey][this.id] = {
          ...this.textEditors[this.sectionKey][this.id],
          controller: null,
          html
        }
      }
    }
  },
  methods: {
    initTextEditors() {
      if (!this.textEditors || !this.textEditors[this.sectionKey]) return
      this.textEditorId = `custom-${this.sectionKey}-${this.id}`
      this.textEditorObject = document.getElementById(this.textEditorId)
      if (this.textEditorObject) {
        this.textEditorObject.addEventListener('click', this.initTextEditor)
      }
    },
    initTextEditor(e) {
      e.preventDefault()

      this.showEditor = true
      if (this.wrapElement) {
        this.wrapElement.classList.add('active')
      }

      if (!this.textEditors[this.sectionKey][this.id]) {
        this.textEditors[this.sectionKey][this.id] = {}
      }
      if (!this.textEditors[this.sectionKey][this.id].controller) {
        // Not Initiated
        if (this.textEditorObject.innerHTML.includes(this.froalaDefaultContent)) {
          this.textEditorObject.innerHTML = ''
        }
        const froalaConfig = JSON.parse(JSON.stringify(FROALA_CONFIG))
        froalaConfig.imageUpload = this.enableImage

        if (this.enableImage) {
          froalaConfig.toolbarButtons = froalaConfig.toolbarButtonsImage
        } else {
          froalaConfig.toolbarButtons = froalaConfig.toolbarButtonsDefault
        }
        froalaConfig.toolbarButtons.moreText.buttonsVisible = froalaConfig.toolbarButtons.moreText.buttons.length

        const controller = new FroalaEditor(`#${this.textEditorId}`, {
          ...froalaConfig,
          events: {
            'initialized': () => {
              if (this.textEditorObject.classList) {
                this.textEditorObject.classList.remove('fr-view')
              }
              controller.toolbar.show()
              controller.events.focus()
            },
            'contentChanged': () => {
              this.$emit('onEditorChanged')
            },
            'destroy': () => {}
          }
        })
        let html = null
        if (this.textEditors[this.sectionKey][this.id].hasOwnProperty('html')) {
          html = this.textEditors[this.sectionKey][this.id].html
        }
        this.textEditors[this.sectionKey][this.id] = {
          ...this.textEditors[this.sectionKey][this.id],
          controller,
          html
        }
      } else {
        // Already Initiated
        this.textEditors[this.sectionKey][this.id].controller.toolbar.show()
      }
    },
    manageTextEditors(e) {
      if (
        this.showEditor &&
        this.textEditors &&
        this.textEditors[this.sectionKey] &&
        this.textEditors[this.sectionKey][this.id] &&
        this.textEditors[this.sectionKey][this.id].controller && 
        this.textEditors[this.sectionKey][this.id].controller.html &&
        this.textEditorObject &&
        !this.isSelfEditorDescendant(e.target)
      ) {
        this.showEditor = false
        if (!this.isEditorDescendant(e.target) && this.wrapElement) {
          this.wrapElement.classList.remove('active')
        }
        this.textEditors[this.sectionKey][this.id].controller.toolbar.hide()
      }
    },
    isEditorDescendant(child) {
      return !!getParentDOM(child, 'custom-textEditors')
    },
    isSelfEditorDescendant(child) {
      return this.textEditorObject.id === child.id || this.textEditorObject.contains(child)
    }
  }
}
</script>